/** Text twin of the beat lanes: every beat-layer event inside the lanes' view as a table row (layer, m:ss.mmm,
 * raw ms, bar.beat.sub) with a Seek button (click or Enter). The layer filter lives in the URL (`beats=`):
 * "lanes" (default: the layers shown as lanes), "all", or one layer. Expanded in agent mode. The complete lists
 * are /api/videos/{id}/beats.{json,csv,md}. */
import { useMemo } from "react";
import { fmtMs } from "../../lib/frames";
import { apiFileUrl } from "../../lib/identity";
import type { FrameController } from "../player/controller";
import {
  BEAT_LAYER_LABELS, BEAT_LAYERS, beatRows, beatSourceText, isBeatLayer, layerTimes, type BeatLayer, type Lane,
  type View,
} from "../timeline/project";
import type { Timeline } from "../types";

export const BEATS_TABLE_MAX = 400;

export function filterLayers(filter: string | null, lanes: Set<Lane>): BeatLayer[] {
  if (filter === "all") return [...BEAT_LAYERS];
  if (filter && isBeatLayer(filter)) return [filter];
  return BEAT_LAYERS.filter((l) => lanes.has(l));
}

export function BeatsTable({ tl, ctrl, view, lanes, filter, setFilter, agent, clock = "MV clock" }: {
  tl: Timeline;
  ctrl: FrameController;
  view: View;
  lanes: Set<Lane>;
  filter: string | null;
  setFilter: (f: string | null) => void;
  agent: boolean;
  /** Which clock the times are on (the Choose page passes "clip clock"). */
  clock?: string;
}) {
  const layers = useMemo(() => filterLayers(filter, lanes), [filter, lanes]);
  const rows = useMemo(() => beatRows(tl, view, layers), [tl, view, layers]);
  const shown = rows.slice(0, BEATS_TABLE_MAX);
  const total = BEAT_LAYERS.reduce((n, l) => n + layerTimes(tl, l).length, 0);
  const bpm = tl.bpm != null ? tl.bpm.toFixed(3) : null;
  const base = `/api/videos/${tl.video_id}/beats`;
  return (
    <details className="ws8-panel" open={agent} data-testid="beats-panel">
      <summary><h2 className="ws8-h2 ws8-inline-h">Beats table</h2></summary>
      <p className="ws8-muted" data-testid="beats-summary">
        {bpm ? `${bpm} BPM · ${tl.meter ?? "?"}${tl.beats_per_bar ? ` (${tl.beats_per_bar} beats per bar)` : ""} · `
          : ""}
        {beatSourceText(tl)}. {total} events. Position = bar.beat.sub (sub = sixteenth, 1 = on the beat).{" "}
        Every event: <a href={apiFileUrl(`${base}.csv`, { download: 1 })} data-testid="beats-csv">CSV</a>,{" "}
        <a href={apiFileUrl(`${base}.md`)} data-testid="beats-md">Markdown</a>,{" "}
        <a href={apiFileUrl(`${base}.json`)} data-testid="beats-json">JSON</a>.
      </p>
      <div className="ws8-row">
        <label className="ws8-inline">
          Layers{" "}
          <select data-testid="beats-layer-filter" value={filter ?? "lanes"}
            onChange={(e) => setFilter(e.target.value === "lanes" ? null : e.target.value)}>
            <option value="lanes">Shown lanes</option>
            <option value="all">All layers</option>
            {BEAT_LAYERS.map((l) => <option key={l} value={l}>{BEAT_LAYER_LABELS[l]}</option>)}
          </select>
        </label>
        <span className="ws8-muted" data-testid="beats-count">
          {rows.length > shown.length ? `First ${shown.length} of ${rows.length}` : `${rows.length}`} events in{" "}
          {fmtMs(view.v0)}–{fmtMs(view.v1)}{rows.length > shown.length ? " (zoom in or pick one layer)" : ""}
        </span>
      </div>
      {rows.length === 0 ? (
        <p data-testid="beats-empty">{total === 0 ? "No beat layers on this timeline yet." : "No events of these layers in this view."}</p>
      ) : (
        <div className="ws8-scroll" tabIndex={0} role="region" aria-label="Beat events in view">
          <table className="ws8-table ws8-beats-table" data-testid="beats-table">
            <caption>Beat-layer events in the view ({clock})</caption>
            <thead>
              <tr><th scope="col">Layer</th><th scope="col">Time</th><th scope="col">ms</th>
                <th scope="col">Bar.beat.sub</th><th scope="col">Seek</th></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.key} data-testid={`beat-row-${r.key}`} data-layer={r.layer} data-t={r.t}>
                  <td>{BEAT_LAYER_LABELS[r.layer]}</td>
                  <td>{fmtMs(r.t)}</td>
                  <td>{r.t}</td>
                  <td>{r.pos ?? "–"}</td>
                  <td>
                    <button type="button" data-testid={`beat-seek-${r.key}`} onClick={() => ctrl.seekMs(r.t)}
                      aria-label={`Seek to ${BEAT_LAYER_LABELS[r.layer]} at ${fmtMs(r.t)}${r.pos ? ` (${r.pos})` : ""}`}>
                      Seek
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
